import { Connections } from './Connections.js';
import { CaptureSettings } from './CaptureSettings.js';
import { CrmSettings } from './CrmSettings.js';
import { ImageFileCapture } from './ImageFileCapture.js';
import { INFO_SETTINGS_TABS, SETTINGS_TABS, type SettingsTabId } from './navigation.js';
import { useConnectionsQuery, useUsageQuery } from './api.js';
import { UsageLimits } from './UsageLimits.js';
import { Integrations } from './Integrations.js';
import { useMeQuery } from './api.js';
import { useOAuthOutcome, type Outcome } from './oauthOutcome.js';
import { Alert } from './ui.js';

/**
 * Configuration, gathered into the four places a customer actually decides something.
 *
 * Connections: which accounts ContactPilot may use. Email Capture: which emails it reads
 * and what happens to them. Image & File Capture: what happens to a contact from a photo,
 * a card or a file. CRM Tags: which tags exist to choose from. Everything that used to sit
 * beside these was either folded into the section holding its one real decision or removed.
 */

/**
 * The consent-callback outcome, in the section the person came back to.
 *
 * A capability upgrade returns `connected` whether or not the extra permission was ticked
 * on the provider's screen, so for an upgrade the sentence is chosen from the grant the
 * server now reports, never from the callback alone.
 */
function OAuthBanner({ outcome, onDismiss }: { outcome: Outcome; onDismiss: () => void }) {
  const { data } = useConnectionsQuery(undefined, { skip: !outcome.upgrade });
  let shown: Outcome | null = outcome;
  if (outcome.upgrade) {
    const connector = data?.connectors.find((c) => c.kind === outcome.upgrade);
    const cap = connector?.capabilities?.find((c) => c.key === 'create_markers');
    if (!cap) shown = null;
    else if (!cap.granted) {
      shown = {
        tone: 'error',
        text: outcome.upgrade === 'gmail'
          ? 'Label creation was not allowed, so nothing changed. Try again and leave the label permission ticked.'
          : 'Category creation was not allowed, so nothing changed. Try again and leave the mailbox settings permission ticked.',
      };
    }
  }
  if (!shown) return null;
  return (
    <div className="space-y-1" aria-live="polite">
      <Alert tone={shown.tone}>{shown.text}</Alert>
      <button
        type="button" onClick={onDismiss}
        className="inline-flex min-h-11 items-center text-sm text-slate-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
      >
        Dismiss
      </button>
    </div>
  );
}

export function Settings({
  tab, onTabChange, privacyOpen = false,
}: { tab: SettingsTabId; onTabChange: (t: SettingsTabId) => void; privacyOpen?: boolean }) {
  const [outcome, dismiss] = useOAuthOutcome();
  // Listed only when the server offers it; a 404 means the usage page is switched off.
  const usage = useUsageQuery();
  const me = useMeQuery();
  const offered = usage.isSuccess && Array.isArray(usage.data?.dimensions);
  const tabs = offered ? [...SETTINGS_TABS, ...INFO_SETTINGS_TABS] : SETTINGS_TABS;

  return (
    <div className="space-y-6">
      {/*
        Horizontally scrollable on a narrow screen rather than wrapped into ragged rows.
        `overflow-x-auto` on the list keeps the page itself from scrolling sideways, which
        is the thing that actually feels broken on a phone.
      */}
      <nav aria-label="Settings sections" className="-mx-4 px-4 sm:mx-0 sm:px-0">
        <ul className="flex gap-0.5 overflow-x-auto border-b border-slate-200 pb-px sm:gap-1">
          {tabs.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                ref={(el) => {
                  if (el && tab === t.id) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                }}
                onClick={() => onTabChange(t.id)}
                aria-current={tab === t.id ? 'page' : undefined}
                className={`min-h-11 whitespace-nowrap border-b-2 px-2.5 text-sm font-medium sm:px-3 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${
                  tab === t.id
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                {t.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {outcome ? <OAuthBanner outcome={outcome} onDismiss={dismiss} /> : null}

      {tab === 'connections' ? <Connections onOpenCrmTags={() => onTabChange('crm-tags')} /> : null}
      {tab === 'email-capture' ? (
        <CaptureSettings
          onOpenConnections={() => onTabChange('connections')}
          onOpenCrmTags={() => onTabChange('crm-tags')}
          onOpenImageFile={() => onTabChange('image-file-capture')}
          privacyOpen={privacyOpen}
        />
      ) : null}
      {tab === 'image-file-capture' ? (
        <ImageFileCapture onOpenCrmTags={() => onTabChange('crm-tags')} />
      ) : null}
      {tab === 'crm-tags' ? <CrmSettings onOpenConnections={() => onTabChange('connections')} /> : null}
      {tab === 'integrations' ? (
        <Integrations canEdit={me.data?.role === 'owner' || me.data?.role === 'admin'} onOpenConnections={() => onTabChange('connections')} />
      ) : null}
      {tab === 'usage' ? (offered ? <UsageLimits /> : usage.isLoading ? null : <Connections onOpenCrmTags={() => onTabChange('crm-tags')} />) : null}
    </div>
  );
}
